import { useEffect, useRef, useState } from 'react';
import { onThemeChange } from '@watkinslabs/watkinsx-components';

export function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// True once the element has scrolled into view (and stays true), for one-shot chart animations.
export function useInView(options = { threshold: 0.25 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!('IntersectionObserver' in window)) { setInView(true); return undefined; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setInView(true); io.disconnect(); }
    }, options);
    io.observe(el);
    return () => io.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, inView];
}

// Counts from 0 to `to` once `run` turns true; jumps straight there under reduced motion.
export function useCountUp(to, run, ms = 1200) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) return undefined;
    if (prefersReducedMotion()) { setV(to); return undefined; }
    let raf; const t0 = performance.now();
    const step = (t) => {
      const k = Math.min(1, (t - t0) / ms);
      setV(to * (1 - (1 - k) ** 3));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, run, ms]);
  return v;
}

// Bumps whenever the site theme changes, so canvases can re-read their colour tokens.
export function useThemeVersion() {
  const [v, setV] = useState(0);
  useEffect(() => {
    const bump = () => setV((n) => n + 1);
    const off = onThemeChange ? onThemeChange(bump) : null;
    const mo = new MutationObserver(bump);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-wx-theme', 'data-carbon-theme'] });
    return () => { if (typeof off === 'function') off(); mo.disconnect(); };
  }, []);
  return v;
}

export function readVar(el, name, fallback) {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  return v || fallback;
}
